import * as angular from 'angular';
import * as Chart from 'chart.js';
import * as _ from 'lodash';
import * as moment from 'moment';
import { LineEquipmentGroup, LineEquipmentItem } from "../../models/counts/lineCounts";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import DateUtilityService from "../../services/utility/dateutility.service";

let requires = [];

interface CountItem extends apsViewModels.CountItem {
  intervalEndTime: number;
}

function updateModel(count: apsViewModels.CountItem): void {
  (<CountItem>count).intervalEndTime = count.intervalEnd.getTime();
}

interface EquipmentGroupRejectsGraphController {
  group: LineEquipmentGroup;
  dataSets: Chart.ChartDataSets[];
  timeZoneId: string;
  getLocalTime(plantTime: Date): Date;
}

export default angular.module('aps.controller.equipmentGroupRejectsGraph', requires)
  .controller('EquipmentGroupRejectsGraphCtrl', equipmentGroupRejectsGraphCtrl)
  .name;

function renderChart(vm: EquipmentGroupRejectsGraphController, $document: angular.IDocumentService): Chart {
  vm.dataSets = createChartDataSets(vm);

  let ctx: any = $document.find('#chart')[0];
  return new Chart(ctx.getContext('2d'), {
    type: 'line',
    data: {
      datasets: vm.dataSets
    },
    options: {
      maintainAspectRatio: true,
      elements: {
        point: {
          hitRadius: 4,
          hoverRadius: 2
        },
        line: {
          tension: 0.001
        }
      },
      tooltips: {
        mode: 'index',
        callbacks: {
          label: (item, data) => data.datasets[item.datasetIndex].label + ': ' + Number(item.yLabel).toFixed(2) + '%'
        }
      },
      // zoom: {
      //   enabled: true,
      //   mode: 'x'
      // },
      // pan: {
      //   enabled: true,
      //   mode: 'x'
      // },
      scales: {
        xAxes: [{
          type: 'time',
          time: {
            tooltipFormat: 'M/D h:mm A',
            displayFormats: {
              minute: 'M/D h:mm A',
              hour: 'M/D h:mm A',
              day: 'M/D'
            }
          },
          ticks: {
            maxRotation: 30,
            minRotation: 30
          }
        }],
        yAxes: [{
          ticks: {
            min: 0
            // ,suggestedMax: 3
          }
        }]
      }
    }
  });
}

function getSortedEquipment(group: LineEquipmentGroup): {
  equipment: LineEquipmentItem,
  rejects: apsViewModels.EquipmentRejects
}[] {
  return _.sortBy(group.rejects, 'lineOrder').map((x) => {
    let equipment: LineEquipmentItem;
    if (group.equipment) {
      for (let key in group.equipment) {
        let equip = group.equipment[key];
        if (equip.name === x.equipmentName && equip.line.name === x.lineName) {
          equipment = equip;
          break;
        }
      }
    }
    return {
      equipment: equipment,
      rejects: x
    };
  });
}

function createChartDataSets(vm: EquipmentGroupRejectsGraphController): Chart.ChartDataSets[] {
  let lineColors = [
    'rgba(181, 14, 9, 0.5)', 'rgba(10, 88, 214, 0.5)', 'rgba(13, 150, 59, 0.5)', 'rgba(181, 115, 9, 0.5)'
  ];

  return getSortedEquipment(vm.group).map((x, i) => {
    return {
      label: `${x.rejects.equipmentName}-${x.rejects.lineName}`,
      fill: false,
      pointRadius: 0,
      data: createIntervalDataPoints(x.rejects.counts, x.equipment, vm),
      borderColor: lineColors[i % 4],
      backgroundColor: lineColors[i % 4],
      borderWidth: 1
    };
  });
}

function updateChart(chart: Chart, vm: EquipmentGroupRejectsGraphController) {
  let currentMinute = moment().startOf('minute');
  let minutesPast10 = currentMinute.minute() % 10;
  let endTime = currentMinute.subtract('minute', minutesPast10).toDate().getTime();
  let updated = false;

  getSortedEquipment(vm.group).forEach((e, i) => {
    let data = vm.dataSets[i].data as Chart.ChartPoint[];
    let lastBucket = getLastBucket(e.equipment, endTime);
    let currentLast = _.last(data);
    if (!lastBucket) {
      return;
    }

    if (!currentLast || currentLast.x <= endTime) {
      data.push(lastBucket);
      updated = true;
    } else if (currentLast.x !== lastBucket.x || currentLast.y !== lastBucket.y) {
      currentLast.x = lastBucket.x;
      currentLast.y = lastBucket.y;
      updated = true;
    }
  });
  if (updated) {
    chart.update();
  }
}

function createIntervalDataPoints(data: apsViewModels.HistoricRejects,
  equipment: LineEquipmentItem,
  vm: EquipmentGroupRejectsGraphController): Chart.ChartPoint[] {
  let points: Chart.ChartPoint[] = [];

  data.inspectCounts.forEach(updateModel);
  data.rejectCounts.forEach(updateModel);

  let inspectCounts = _.sortBy(<CountItem[]>data.inspectCounts, x => x.intervalEndTime);
  let rejectCounts = <CountItem[]>data.rejectCounts;

  let startTime = inspectCounts[0].intervalEndTime;
  let endTime = inspectCounts[inspectCounts.length - 1].intervalEndTime;
  let currentTime = startTime;
  let currentIndex = 0;

  let interval = null;
  let intervalStart = null;
  inspectCounts.forEach((c, i) => {
    if (i === 0) {
      intervalStart = c.intervalEndTime;
      return;
    }

    let currentInterval = c.intervalEndTime - intervalStart;
    if (!interval || interval > currentInterval) {
      interval = currentInterval;
    }
    intervalStart = c.intervalEndTime;
  });
  if (!interval) {
    interval = 600000;
  }

  while (currentTime <= endTime && currentIndex < inspectCounts.length) {
    let inspectCount = 0;
    let inspect = inspectCounts[currentIndex];
    if (inspect.intervalEndTime === currentTime) {
      inspectCount = inspect.value;
      currentIndex++;
    }
    let rejectCount = 0;
    let reject = _.find(rejectCounts, { intervalEndTime: currentTime });
    if (reject) {
      rejectCount = reject.value;
    }
    let rejectPercent = 0;
    if (inspectCount !== 0 && rejectCount !== 0) {
      rejectPercent = rejectCount * 100 / inspectCount;
    }
    pushPoint(currentTime, rejectPercent, points, vm);
    currentTime += interval;
  }

  if (equipment) {
    setLastBucket(equipment, points, vm, endTime);
  }

  return points;
}

function getLastBucket(equipment: LineEquipmentItem, endTime: number): Chart.ChartPoint {
  if (!equipment) {
    return null;
  }
  let lastInspectCounts = equipment.inspectCount.counts.filter(x => x.intervalStartTime >= endTime);
  if (lastInspectCounts.length === 0) {
    return null;
  }
  let maxTime: number = _.max(_.map(lastInspectCounts, 'intervalEndTime'));
  let inspectCount: number = _.sum(_.map(lastInspectCounts, 'value'));
  let rejectCount = 0;
  let lastRejectCounts = equipment.rejectCount.counts.filter(x =>
    x.intervalStartTime >= endTime && x.intervalEndTime <= maxTime);
  if (lastRejectCounts.length > 0) {
    rejectCount = _.sum(_.map(lastRejectCounts, 'value'));
  }
  let rejectPercent = 0;
  if (inspectCount !== 0 && rejectCount !== 0) {
    rejectPercent = rejectCount * 100 / inspectCount;
  }
  return { x: maxTime, y: rejectPercent };
}

function setLastBucket(equipment: LineEquipmentItem,
  points: Chart.ChartPoint[],
  vm: EquipmentGroupRejectsGraphController,
  endTime: number) {
  let lastBucket = getLastBucket(equipment, endTime);
  if (lastBucket) {
    pushPoint(lastBucket.x as number, lastBucket.y as number, points, vm);
  }
}

function pushPoint(time: number,
  rejectPercent: number,
  points: Chart.ChartPoint[],
  vm: EquipmentGroupRejectsGraphController) {
  let localTime = vm.getLocalTime(new Date(time)).getTime();
  points.push({ x: localTime, y: rejectPercent });
}

function equipmentGroupRejectsGraphCtrl($scope: angular.IScope,
  $timeout: angular.ITimeoutService,
  $document: angular.IDocumentService): EquipmentGroupRejectsGraphController {
  let vm = this as EquipmentGroupRejectsGraphController;

  function getLocalTime(plantTime: Date): Date {
    return DateUtilityService.timeZoneToLocal(plantTime, vm.timeZoneId).toDate();
  }
  vm.getLocalTime = getLocalTime;

  $timeout(function () {
    let chart = renderChart(vm, $document);

    function groupUpdated() {
      updateChart(chart, vm);
    }

    vm.group.groupUpdated = groupUpdated;
  });

  $scope.$on('$destroy', () => {
    vm.group.groupUpdated = null;
  });
  return vm;
}


// WEBPACK FOOTER //
// ./src/app/controllers/counts/equipmentGroupRejectsGraph.controller.ts